import { useEffect, useMemo, useState } from 'react';

import { Tooltip } from 'antd/lib';
import { Info } from 'react-feather';

import { Typography, Wrapper } from '@/components';
import { useTranslation } from 'react-i18next';
import { useCuration } from '@/hooks/client';

import styles from './CampaignPageCuration.module.css';
import { Curation } from './components/Curation';
import { Tabs } from './components/Tabs';
import { filteredTab } from './utils';

interface CampaignPageCurationProps {
  campaignId: string;
}

export function CampaignPageCuration({ campaignId }: CampaignPageCurationProps) {
  const [currentTab, setCurrentTab] = useState('CURATION');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { t } = useTranslation();

  const { data = {}, isLoading, refetch } = useCuration(campaignId);

  const memoData = useMemo(() => data?.curation || [], [data]);

  useEffect(() => {
    if (!isLoading && !hasLoadedOnce) {
      if (
        filteredTab(memoData, 'CURATION').length ||
        (!filteredTab(memoData, 'ACCEPTED').length && !filteredTab(memoData, 'REJECTED').length)
      ) {
        return undefined;
      }
      if (filteredTab(memoData, 'ACCEPTED').length) {
        setCurrentTab('ACCEPTED');
      } else if (filteredTab(memoData, 'REJECTED').length) {
        setCurrentTab('REJECTED');
      }

      setHasLoadedOnce(true);
    }
    return undefined;
  }, [isLoading, memoData, hasLoadedOnce]);

  const TABS = [
    {
      count: filteredTab(memoData, 'CURATION').length,
      value: t('campaignTab.pending'),
      key: 'CURATION'
    },
    {
      count: filteredTab(memoData, 'ACCEPTED').length,
      value: t('campaignTab.approved'),
      key: 'ACCEPTED'
    },
    {
      count: filteredTab(memoData, 'REJECTED').length,
      value: t('campaignTab.declined'),
      key: 'REJECTED'
    }
  ];

  return (
    <Wrapper
      heading={t('campaignPageCuration.heading')}
      isLoading={isLoading}
      campaignId={campaignId}
    >
      <div className={styles.campaignPageCurationNavigation}>
        <Tabs setCurrentTab={setCurrentTab} currentTab={currentTab} tabs={TABS} />
      </div>

      <div className={styles.campaignPageCurationList}>
        {filteredTab(memoData, currentTab).length ? (
          <>
            <div className={styles.campaignPageCurationListRow} data-tab={currentTab}>
              <div>
                <Typography sizes={['label-secondary']}>
                  {t('campaignPageCuration.influencer')}
                </Typography>
              </div>
              <div>
                <Typography sizes={['label-secondary']}>
                  {t('campaignPageCuration.beats')}
                </Typography>
              </div>
              <div>
                <Typography sizes={['label-secondary']}>
                  <div className={styles.flex}>
                    {t('campaignPageCuration.followers')}
                    <Tooltip title={t('campaignPageCuration.tooltip')}>
                      <Info size={16} />
                    </Tooltip>
                  </div>
                </Typography>
              </div>
              <div>
                <Typography sizes={['label-secondary']}>
                  <div className={styles.flex}>
                    {t('campaignPageCuration.engagement')}
                    <Tooltip title={t('campaignPageCuration.tooltip')}>
                      <Info size={16} />
                    </Tooltip>
                  </div>
                </Typography>
              </div>
            </div>

            {filteredTab(memoData, currentTab).map((curation: Api.Client.Curation) => (
              <div
                className={styles.campaignPageCurationListCuration}
                key={`curator-${curation.curation_id}`}
              >
                <Curation
                  onSuccess={refetch}
                  curation={curation}
                  tab={currentTab}
                  campaignId={campaignId}
                />
              </div>
            ))}
          </>
        ) : (
          <Typography sizes={['h4']}>{t(`campaignPageCuration.notFound.${currentTab}`)}</Typography>
        )}
      </div>
    </Wrapper>
  );
}
